import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import loadable from '@loadable/component';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { trackProduct } from '@jsmdg/tracking';
import { Breakpoint, useBreakpoint } from '@jsmdg/yoshi';
import { ProductPromotionType } from '../../../shared/enums';
import { isBox } from '../../../shared/product-safeguards';
import { type Reviews } from '../../../shared/reviews';
import { type Product } from '../../../shared/types';
import { usePrice } from '../../hooks/usePrice';
import { useProduct } from '../../hooks/useProduct';
import { useReviews } from '../../hooks/useReviews';
import { type ProductDetailContext } from '../../types/types';
import { AccordionContainer } from '../AccordionContainer';
import { ErrorBoundary } from '../ErrorBoundary';
import { GiftBoxRelatedProductsFallback } from '../GiftBoxRelatedProducts/GiftBoxRelatedProductsFallback';
import { StrikePriceInfo } from '../Price/StrikePriceInfo';
import { ProductMarkup } from '../ProductMarkup/ProductMarkup';
import { ProductServiceInfo } from '../ProductServiceInfo/ProductServiceInfo';
import { useProductInfoAccordions } from './ProductInfoContentBuilder';

const LoadableGiftBoxRelatedProducts = loadable(
    async () => import('../GiftBoxRelatedProducts/GiftBoxRelatedProducts'),
    {
        fallback: <GiftBoxRelatedProductsFallback />,
    },
);

const ProductInfo = (): JSX.Element => {
    const product = useProduct();
    const { price } = usePrice();
    const { pageUrl } = useFragmentContext<ProductDetailContext>();
    const { reviews } = useReviews();
    const isClubDeal = product.promotionType === ProductPromotionType.CustomerClub;

    const productMarkupData: Product & Reviews = {
        ...product,
        ...((reviews || {}) as Reviews),
    };
    const isMobile = !useBreakpoint(Breakpoint.SM);
    const accordions = useProductInfoAccordions(product);

    useEffect(() => {
        trackProduct(
            {
                ...product,
                price,
                images: product.images.map(image => ({ ...image, absoluteUrl: image.src })),
            },
            window.location.origin,
        );
    }, [price, product]);

    return (
        <>
            <ProductServiceInfo content={product.productInformation.services} />
            {isMobile && !isClubDeal && <StrikePriceInfo numTickets={1} />}
            <AccordionContainer product={product} accordions={accordions} isMobile={isMobile} />

            {isBox(product) && !product.voucherPartnerInformation.iframeUrl && (
                <ErrorBoundary>
                    <LoadableGiftBoxRelatedProducts
                        productId={product.productId}
                        inPreview={product.inPreview}
                    />
                </ErrorBoundary>
            )}

            <div className="pt-3x theme-text-caption-text">
                <FormattedMessage defaultMessage="Artikelnummer" />: {product.manufacturerSku}
            </div>

            <ProductMarkup markupData={productMarkupData} pageUrl={pageUrl} price={price} />
        </>
    );
};

export { ProductInfo };
